import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Mail, Phone, Users, ExternalLink, AlertTriangle } from "lucide-react";
import { MapLink } from "@/components/MapLink";
import AndringsforslagButton from "@/components/AndringsforslagButton";
import MediaLink from "@/components/MediaLink";
import SocialLinks from "@/components/SocialLinks";
import EntryFootnote from "@/components/EntryFootnote";
import ExpandableBeskrivning from "@/components/ExpandableBeskrivning";
import GalleriStrip from "@/components/GalleriStrip";
import { useTranslatedObjects } from "@/hooks/useTranslatedContent";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export default function ForeningarEvenemang() {
  const [evenemang, setEvenemang] = useState([]);
  const [historik, setHistorik] = useState([]);
  const [foreningar, setForeningar] = useState([]);
  const [visaHistorik, setVisaHistorik] = useState(false);

  usePageMeta({
    title: "Föreningar & evenemang i bygden | vidalv.live",
    description: "Föreningar, kommande evenemang och tidigare händelser i bygden vid Fjällsjöälven. Marknader, möten, idrott, kultur.",
    path: "/foreningar-evenemang",
  });

  useEffect(() => {
    document.title = "Föreningar – vidalv.live";
    axios.get(`${API}/evenemang`).then(r => setEvenemang(r.data)).catch(() => {});
    axios.get(`${API}/evenemang/historik`).then(r => setHistorik(r.data)).catch(() => {});
    axios.get(`${API}/foreningar`).then(r => setForeningar(r.data)).catch(() => {});
  }, []);

  const [evenemangT] = useTranslatedObjects(evenemang, ["titel", "beskrivning"]);
  const [historikT]  = useTranslatedObjects(historik, ["titel"]);
  const [foreningarT]= useTranslatedObjects(foreningar, ["namn", "beskrivning"]);
  // Etappe 4.9 — alphabetisk sortering (svensk locale: Å, Ä, Ö efter Z).
  const foreningarSorted = [...foreningarT].sort((a, b) =>
    (a.namn || "").localeCompare(b.namn || "", "sv", { sensitivity: "base" })
  );

  return (
    <div data-testid="foreningar-evenemang-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Föreningar</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3 leading-[1.05]">Det händer mer än man tror</h1>
      <p className="text-sm text-bark mb-10 max-w-2xl leading-relaxed">
        Idrottsföreningar, byalag, handarbetscirklar, jaktlag, hembygdsföreningar. Här ser du vad som finns och vad som händer.
      </p>

      {/* Föreningar – primärt innehåll på sidan, sektionen visas först */}
      <section data-testid="foreningar-list" className="mb-14">
        <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide leading-tight mb-2">Föreningar i bygden</h2>
        <p className="text-sm text-bark mb-6">Det mesta är öppet för den som vill vara med. {foreningar.length > 0 && `(${foreningar.length} st)`}</p>
        <div className="flex flex-col gap-4">
          {foreningar.length === 0 && (
            <div data-testid="no-foreningar" className="bg-parchment border border-warm-border rounded-sm p-6 text-center">
              <p className="text-sm text-bark/70">Inga föreningar registrerade ännu.</p>
            </div>
          )}
          {foreningarSorted.map((f) => (
            <div key={f.id} data-testid={`forening-${f.id}`} className="bg-parchment border border-warm-border rounded-sm p-5 sm:p-6">
              <h3 className="font-heading text-xl font-semibold text-ink mb-1">{f.namn}</h3>
              {f.ort && <p className="text-xs text-bark mb-2 flex items-center gap-1"><MapPin size={14} /> {f.ort}</p>}
              <ExpandableBeskrivning kort={f.kortbeskrivning} full={f.beskrivning} testId={`forening-${f.id}-besk`} className="mb-3" />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-bark">
                {f.kontaktperson && <span>Kontakt: {f.kontaktperson}</span>}
                {f.kontakt_epost && <a href={`mailto:${f.kontakt_epost}`} className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><Mail size={14} /> {f.kontakt_epost}</a>}
                {f.kontakt_telefon && <a href={`tel:${f.kontakt_telefon.replace(/\s/g,"")}`} className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><Phone size={14} /> {f.kontakt_telefon}</a>}
                <MapLink name={f.namn} />
              </div>
              {/* Etappe 4.1 — universella sociala länkar (Web/FB/IG/YT). Visas endast om fyllda. */}
              <SocialLinks
                webbplats={f.webbplats}
                facebook_url={f.facebook_url}
                instagram_url={f.instagram_url}
                youtube_url={f.youtube_url}
              />
              {/* Etappe 4.11 — galleri (visas endast om bilder finns) */}
              {Array.isArray(f.galleri) && f.galleri.length > 0 && (
                <div className="mt-3">
                  <GalleriStrip bilder={f.galleri} galleriAudio={f.galleri_audio} alt={f.namn} />
                </div>
              )}
              <MediaLink media_url={f.media_url} media_typ={f.media_typ} media_beskrivning={f.media_beskrivning} />
              <EntryFootnote
                taggar={f.taggar}
                kalla={f.kalla || f.kalltext}
                senast={f.senast_andrad || f.senast_kontrollerad}
                uppdaterad_av={f.andrad_av}
                testIdPrefix={`forening-${f.id}`}
              />
              <div className="mt-4 pt-3 border-t border-warm-border/70">
                <AndringsforslagButton typ="forening" referensId={f.id} referensNamn={f.namn} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Evenemang – sekundärt på sidan, ligger under föreningarna */}
      <section data-testid="evenemang-list" className="mb-14">
        <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide leading-tight mb-2">Kommande evenemang</h2>
        <p className="text-sm text-bark mb-6">Marknader, möten, fester och träffar i bygden. Passerade datum försvinner automatiskt från listan men sparas i arkivet nedan.</p>
        <div className="flex flex-col gap-4">
          {evenemang.length === 0 && (
            <div data-testid="no-upcoming" className="bg-parchment border border-warm-border rounded-sm p-6 text-center">
              <p className="text-sm text-bark/70">Just nu är inga evenemang inplanerade. Fortsätt titta – nya dyker upp snart.</p>
            </div>
          )}
          {evenemangT.map((ev) => (
            <Link to={`/evenemang/${ev.id}`} key={ev.id} data-testid={`event-${ev.id}`} className="block bg-parchment border border-warm-border rounded-sm p-5 sm:p-6 no-underline hover:border-bark/40 transition-all group">
              <span className="inline-block text-xs font-body font-semibold uppercase tracking-wide px-2.5 py-1 rounded-sm mb-3 bg-bone-alt text-pine">
                Evenemang
              </span>
              <h3 className="font-heading text-xl font-semibold text-ink mb-2 group-hover:text-falu transition-colors">{ev.titel}</h3>
              <p className="text-sm text-bark mb-3 leading-relaxed">{ev.kortbeskrivning || ev.beskrivning}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-bark/70">
                {ev.datum && <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(ev.datum)}{ev.tid ? `, ${ev.tid}` : ""}</span>}
                {ev.plats && <span className="flex items-center gap-1"><MapPin size={14} /> {ev.plats}</span>}
                {ev.arrangor && <span className="flex items-center gap-1"><Users size={14} /> {ev.arrangor}</span>}
              </div>
            </Link>
          ))}
        </div>

        {evenemang.length > 0 && (
          <div data-testid="foreningar-trust-notice" className="mt-6 bg-bone-alt/60 border-l-3 border-falu/60 rounded-sm p-4 flex gap-3 items-start">
            <AlertTriangle size={16} className="text-falu shrink-0 mt-0.5" />
            <p className="text-xs text-bark leading-relaxed">
              <strong>Uppgifter kan ändras.</strong> Kontrollera alltid tid, plats och kontaktuppgifter hos arrangören innan du åker.
            </p>
          </div>
        )}

        {historik.length > 0 && (
          <div data-testid="historik-block" className="mt-8">
            <button
              data-testid="toggle-historik"
              onClick={() => setVisaHistorik(v => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-bark/60 hover:text-ink uppercase tracking-[0.2em] border-b border-dashed border-bark/30 pb-0.5 transition-colors"
            >
              {visaHistorik ? "Göm arkiv" : `Visa arkiv (${historik.length} genomförda)`}
            </button>
            {visaHistorik && (
              <div className="mt-5 flex flex-col gap-3">
                {historikT.map(ev => (
                  <div key={ev.id} data-testid={`historik-${ev.id}`} className="bg-bone-alt/40 border border-warm-border/50 rounded-sm p-4 opacity-75">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-body font-semibold uppercase tracking-wide text-bark/50">Genomfört</span>
                      {ev.datum && <span className="text-[11px] text-bark/60">{formatDate(ev.datum)}</span>}
                    </div>
                    <h4 className="font-heading text-base font-semibold text-ink/80">{ev.titel}</h4>
                    {ev.plats && <p className="text-xs text-bark/60 mt-1">{ev.plats}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
